// @owners { team: patient-health }
import { Row, SmSpacing } from '@alto/design-system';
import { useRemoveFromCart } from '@alto/features';
// eslint-disable-next-line @alto/no-pocky-import
import { Button, LoadingButton, Modal, ModalContent, ModalFooter, Text, toaster } from '@alto/pocky';
import React, { useCallback, useEffect } from 'react';
import { closeModal } from '~shared/actions/modal';
// eslint-disable-next-line import/no-deprecated
import { archivePrescription } from '~shared/actions/prescriptions';
import { prescriptionToItemKey } from '~shared/features/checkout/helpers';
import { partitionUpcomingDeliveries } from '~shared/features/my-meds/helper';
import { formatRelativeDate } from '~shared/helpers/date';
import { getErrorMessage } from '~shared/helpers/helper';
import { useDispatchShared } from '~shared/store';
import { type APIError } from '~shared/types/APIError';
import { type LightDelivery, type Prescription } from '~shared/types/clients';

type Props = {
  readonly archivingPrescription: boolean;
  readonly archivePrescriptionError: APIError | null | undefined;
  readonly deliveries: LightDelivery[];
  readonly onClose: () => void;
  readonly prescription: Prescription;
};

export const ArchivePrescriptionModal = ({
  archivingPrescription,
  archivePrescriptionError = null,
  deliveries,
  onClose,
  prescription,
}: Props) => {
  const dispatch = useDispatchShared();
  const { handleRemoveFromCart } = useRemoveFromCart();

  useEffect(() => {
    const message = getErrorMessage(archivePrescriptionError);

    if (message) {
      toaster.toast({
        description: 'Please contact us if this error persists.',
        kind: 'danger',
        title: message,
      });
    }
  }, [archivePrescriptionError]);

  const handleArchive = useCallback(() => {
    // eslint-disable-next-line import/no-deprecated, promise/catch-or-return, promise/always-return
    dispatch(archivePrescription(prescription)).then(() => {
      handleRemoveFromCart({ item: prescriptionToItemKey(prescription.id) });
      dispatch(closeModal('ARCHIVE_PRESCRIPTION_MODAL'));
    });
  }, [dispatch, handleRemoveFromCart, prescription]);

  const { medication_name } = prescription;
  const { potentialCanceledDeliveries, upcomingDeliveries } = partitionUpcomingDeliveries(deliveries);

  return (
    <Modal
      onClose={onClose}
      showing
      // eslint-disable-next-line sonarjs/no-nested-template-literals
      title={`Archive ${medication_name ? `Rx: ${medication_name}` : 'Prescription'}`}
    >
      <ModalContent textAlignment="left">
        <Text>
          If you don&#39;t need this prescription anymore, archive it! Archiving keeps your med list easy to read.
        </Text>
        <Text>You can unarchive this at any time when viewing your inactive prescriptions.</Text>
        {potentialCanceledDeliveries.length > 0 ? (
          <Text>
            Archiving this will also remove {medication_name} from these deliveries:
            <ul>
              {potentialCanceledDeliveries.map((d) => (
                <li key={`potential ${d.id}`}>
                  <Text>{formatRelativeDate(d.date || '', { includeRelative: true })}</Text>
                </li>
              ))}
            </ul>
          </Text>
        ) : null}
        {upcomingDeliveries.length > 0 ? (
          <Text>
            You&#39;ll still receive these upcoming deliveries:
            <ul>
              {upcomingDeliveries.map((d) => (
                <li key={`upcoming ${d.id}`}>
                  <Text>{formatRelativeDate(d.date || '', { includeRelative: true })}</Text>
                </li>
              ))}
            </ul>
          </Text>
        ) : null}
      </ModalContent>

      <ModalFooter>
        <Row right>
          <Button
            kind="secondary"
            onClick={onClose}
          >
            Cancel
          </Button>
          <SmSpacing />
          <LoadingButton
            loading={archivingPrescription}
            loadingText="Archiving..."
            onClick={handleArchive}
          >
            Archive
          </LoadingButton>
        </Row>
      </ModalFooter>
    </Modal>
  );
};
